import { MapPin, Check, Star } from "lucide-react";
import { NRW_CITIES, OTHER_CITIES } from "@/lib/siteConfig";

export const CoverageSection = () => {
  return (
    <section id="coverage" data-testid="coverage-section" className="ss-section bg-white">
      <div className="ss-container">
        <div className="max-w-3xl">
          <span className="ss-eyebrow">Einsatzgebiet</span>
          <h2 className="ss-h2 mt-4 text-slate-900">
            Security Staging in ganz{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Nordrhein-Westfalen</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[var(--ss-gold)]/40 -z-0" aria-hidden="true" />
            </span>{" "}
            – und bundesweit.
          </h2>
          <p className="ss-lead mt-5">
            Unser Schwerpunkt ist <strong>Nordrhein-Westfalen</strong>: Ob{" "}
            <strong>Alarmanlage in Dortmund</strong>, <strong>Security Staging in Köln</strong>,{" "}
            <strong>Leerstandsschutz in Düsseldorf</strong>, <strong>Essen</strong>,{" "}
            <strong>Duisburg</strong>, <strong>Bochum</strong> oder das gesamte Ruhrgebiet – wir sind schnell vor Ort. Darüber hinaus betreuen wir deutschlandweit Immobilien in allen großen Metropolregionen.
          </p>
        </div>

        {/* NRW block - highlighted */}
        <div
          id="nrw"
          className="mt-12 rounded-3xl bg-gradient-to-br from-[#071A2B] to-[#0A2340] text-white p-6 sm:p-10 border border-[var(--ss-gold)]/25 shadow-[0_25px_70px_rgba(7,26,43,0.25)] relative overflow-hidden"
          data-testid="coverage-nrw-block"
        >
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[var(--ss-gold)]/10 blur-3xl" aria-hidden="true" />

          <div className="relative grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ss-gold)]/15 border border-[var(--ss-gold)]/30 px-3 py-1.5">
                <Star className="h-3.5 w-3.5 text-[var(--ss-gold)]" fill="currentColor" />
                <span className="text-xs font-semibold uppercase tracking-widest text-[var(--ss-gold)]">
                  Schwerpunktregion
                </span>
              </div>
              <h3 className="font-display font-semibold text-2xl sm:text-3xl text-white mt-4">
                Nordrhein-Westfalen
              </h3>
              <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">
                Als Anbieter mit Sitz in <strong className="text-white">Dortmund</strong> haben wir Nordrhein-Westfalen als Kernregion. Kurze Anfahrtszeiten, tiefe Marktkenntnis und flexible Terminierung – ideal für Makler, Eigentümer und Bauträger in ganz NRW.
              </p>
              <div className="mt-5 space-y-2 text-sm text-slate-200">
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--ss-gold)] mt-0.5 shrink-0" strokeWidth={3} />
                  <span>Ruhrgebiet: Dortmund, Essen, Duisburg, Bochum, Gelsenkirchen …</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--ss-gold)] mt-0.5 shrink-0" strokeWidth={3} />
                  <span>Rheinschiene: Köln, Düsseldorf, Bonn, Leverkusen, Neuss …</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--ss-gold)] mt-0.5 shrink-0" strokeWidth={3} />
                  <span>Bergisches Land, Münsterland, Ostwestfalen &amp; Südwestfalen</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
                <MapPin className="h-3.5 w-3.5 text-[var(--ss-gold)]" />
                NRW-Städte im Einsatz
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="coverage-nrw-city-grid">
                {NRW_CITIES.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ss-gold)]/25 bg-white/[0.06] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--ss-gold)]/15 hover:border-[var(--ss-gold)]/50 transition-colors"
                    data-testid={`coverage-nrw-${c
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/ü/g, "u")
                      .replace(/ä/g, "a")
                      .replace(/ö/g, "o")}-chip`}
                  >
                    <MapPin className="h-3 w-3 text-[var(--ss-gold)]" />
                    {c}
                  </span>
                ))}
                <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] px-3 py-1.5 text-sm text-slate-300">
                  + weitere NRW-Städte &amp; Landkreise
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Rest of Germany block */}
        <div className="mt-8 rounded-2xl bg-slate-50 border border-slate-200/80 p-6 sm:p-8" data-testid="coverage-other-block">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MapPin className="h-4 w-4 text-slate-500" />
                <span className="uppercase tracking-wider">Bundesweit im Einsatz</span>
              </div>
              <h3 className="font-display font-semibold text-lg sm:text-xl text-slate-900 mt-2">
                Auch außerhalb von NRW – deutschlandweit für Sie da.
              </h3>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="coverage-other-city-grid">
            {OTHER_CITIES.map((c) => (
              <span
                key={c}
                className="ss-chip"
                data-testid={`coverage-other-${c
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/ü/g, "u")
                  .replace(/ä/g, "a")
                  .replace(/ö/g, "o")}-chip`}
              >
                <MapPin className="h-3.5 w-3.5 text-slate-500" />
                {c}
              </span>
            ))}
            <span className="ss-chip">+ weitere Regionen</span>
          </div>

          <p className="mt-5 text-xs text-slate-500 leading-relaxed">
            Ihre Stadt oder Region ist nicht dabei? Rufen Sie uns an – wir betreuen bundesweit auch Landkreise und kleinere Gemeinden.
          </p>
        </div>

        {/* Object types */}
        <div className="mt-10 grid sm:grid-cols-3 gap-3 text-sm text-slate-700">
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 text-[#EAB308] mt-0.5 shrink-0" strokeWidth={3} />
            <span>Für Wohnungen, Einfamilienhäuser, Villen &amp; Neubauten</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 text-[#EAB308] mt-0.5 shrink-0" strokeWidth={3} />
            <span>Terminierung nach Objekt und Verkaufsphase – flexibel und kurzfristig</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-4 w-4 text-[#EAB308] mt-0.5 shrink-0" strokeWidth={3} />
            <span>Auch Gewerbeobjekte und Bauträger-Projekte in ganz Deutschland</span>
          </div>
        </div>
      </div>
    </section>
  );
};
